import { definePublicSite, } from "@logscord/public-site";
import { LegalDocsMetadata } from "./content.js";
import { PublicLocales, formatLocale, } from "./locales.js";
import { createLogscordApplicationStructuredData } from "./structuredData/application.js";
import { createLogscordPublicHeadExtensions } from "./headExtensions.js";
export const DefaultLogscordPublicSiteUrl = "https://logscord.com";
export const DefaultLogscordPublicDefaultImagePath = "/assets/img/banner.png";
export const PublicSitemapLastModified = "2026-07-31";
export const LogscordLocalizedRedirectPolicy = {
    mode: "redirect",
    redirect: "accept-language",
    xDefault: "default-locale",
};
export const LogscordHomeLocalePolicy = {
    mode: "indexable",
    xDefault: "unlocalized",
};
const StaticCache = {
    maxAge: 300,
    staleIfError: 300,
};
export const LogscordPublicDynamicCachePolicy = {
    kind: "public",
    maxAgeSeconds: 60,
    staleIfErrorSeconds: 300,
};
const DynamicCache = {
    maxAge: LogscordPublicDynamicCachePolicy.maxAgeSeconds,
    staleIfError: LogscordPublicDynamicCachePolicy.staleIfErrorSeconds,
};
const seo = (name) => ({
    title: `seo.${name}.title`,
    description: `seo.${name}.description`,
});
const sitemap = (priority, changeFrequency = "monthly") => ({ priority, changeFrequency });
function privateRoute(path, component) {
    return { path, component, private: true };
}
function createRoutes() {
    return {
        home: {
            path: "/",
            component: "intro",
            browserComponent: "app-home",
            localized: LogscordHomeLocalePolicy,
            render: { kind: "hybrid", prerender: "all", fallback: "ssr" },
            seo: seo("home"),
            sitemap: sitemap(1, "weekly"),
            markdown: "home",
            llms: "include",
        },
        features: {
            path: "/features", component: "features", seo: seo("features"),
            sitemap: sitemap(0.9), markdown: "features",
        },
        vision: {
            path: "/vision", component: "vision", seo: seo("vision"),
            sitemap: sitemap(0.8), markdown: "vision",
        },
        compare: {
            path: "/compare", component: "compare", seo: seo("compare"),
            sitemap: sitemap(0.8), markdown: "compare",
        },
        trust: {
            path: "/trust", component: "trust", loader: "trust", cache: DynamicCache,
            seo: seo("trust"), sitemap: sitemap(0.8, "weekly"), markdown: "trust",
            llms: "index-only",
        },
        servers: {
            path: "/servers", component: "servers", loader: "servers", cache: DynamicCache,
            seo: seo("servers"), sitemap: sitemap(0.7, "daily"), markdown: "servers",
            llms: "index-only",
        },
        server: {
            path: "/servers/:serverRef", component: "server", loader: "server",
            source: "servers", head: "server", cache: false, seo: seo("server"),
            sitemap: sitemap(0.6, "daily"), markdown: "server", llms: false,
        },
        team: {
            path: "/team", component: "team", loader: "team", head: "team",
            cache: DynamicCache, seo: seo("team"), sitemap: sitemap(0.6),
            markdown: "team", llms: "index-only",
        },
        docs: {
            path: "/docs", component: "docs", loader: "articles", cache: DynamicCache,
            seo: seo("docs"), sitemap: sitemap(0.8, "weekly"), markdown: "docs",
            llms: "index-only",
        },
        article: {
            path: "/docs/:slug", component: "doc", loader: "article", source: "articles",
            head: "article", cache: DynamicCache, seo: seo("docs"),
            sitemap: sitemap(0.7, "weekly"), markdown: "article", llms: "include",
        },
        ...Object.fromEntries(LegalDocsMetadata.map((doc) => [
            `legal.${doc.slug}`,
            {
                path: `/${doc.slug}`,
                name: doc.slug,
                component: "legal",
                content: doc.contentKey,
                seo: seo(doc.slug),
                sitemap: {
                    changeFrequency: doc.sitemap.changefreq,
                    priority: doc.sitemap.priority,
                },
                markdown: { content: doc.contentKey },
                llms: { visibility: "optional", full: "include" },
            },
        ])),
        "app.profile": privateRoute("/profile", "profile"),
        "app.logs": privateRoute("/logs", "logs"),
        "app.dashboard.new": privateRoute("/dashboards/new", "dashboard-builder"),
        "app.dashboard.edit": privateRoute("/dashboards/:id/edit", "dashboard-builder"),
        "app.sanctions": privateRoute("/sanctions", "sanctions"),
        "app.settings": privateRoute("/settings", "settings"),
        "app.settings.policies": privateRoute("/settings/policies", "log-access-policies"),
        "app.tools": privateRoute("/tools", "tools"),
        "app.tools.community-engagement": privateRoute("/tools/community-engagement", "community-engagement"),
        "app.tools.permissions-simulator": privateRoute("/tools/permissions-simulator", "permissions-simulator"),
        "app.tools.bulk-role-permissions": privateRoute("/tools/bulk-role-permissions", "bulk-role-permissions"),
        "app.users": privateRoute("/users", "member-directory"),
        "app.user": privateRoute("/users/:id/:menu?", "user"),
        "app.role": privateRoute("/roles/:id", "role-details"),
        "app.channels": privateRoute("/channels", "all-channels"),
        "app.channel": privateRoute("/channels/:id", "channel"),
        "app.report": privateRoute("/report", "report"),
        "app.graph": privateRoute("/graph", "graph"),
        "app.voice": privateRoute("/voice", "voice"),
        "redirect.automates": {
            from: "/automates", to: "/", status: 308,
        },
        "redirect.permissions-simulator": {
            from: "/permissions-simulator", to: "/tools/permissions-simulator", status: 308,
        },
    };
}
export function createLogscordPublicSite(options = {}) {
    const defaultLocaleCode = options.defaultLocaleCode
        ?? PublicLocales.find((locale) => locale.locale === options.defaultLocale)?.code
        ?? "en";
    const siteUrl = (options.siteUrl ?? DefaultLogscordPublicSiteUrl).replace(/\/$/, "");
    const configuredImage = options.assets?.defaultImage ?? DefaultLogscordPublicDefaultImagePath;
    const defaultImage = /^https?:\/\//i.test(configuredImage)
        ? configuredImage
        : `${siteUrl}${configuredImage.startsWith("/") ? configuredImage : `/${configuredImage}`}`;
    const site = definePublicSite({
        origin: siteUrl,
        locales: PublicLocales,
        defaultLocale: defaultLocaleCode,
        lastModified: options.sitemapLastModified ?? PublicSitemapLastModified,
        seo: {
            name: "LogsCord",
            defaultImage,
            themeColor: "#6f5cff",
            author: "LogsCord",
            applicationName: "LogsCord",
            twitterCard: "summary_large_image",
            translate: formatLocale,
            notFound: seo("not_found"),
            unavailable: {
                title: "seo.default.title", description: "seo.default.description",
            },
            private: {
                title: "seo.default.title", description: "seo.private.description",
            },
            structuredData: ({ copy }) => createLogscordApplicationStructuredData(siteUrl, copy.description),
        },
        defaults: {
            localized: LogscordLocalizedRedirectPolicy,
            render: "ssg",
            cache: StaticCache,
            sitemap: true,
            markdown: "seo",
            llms: "include",
        },
        robots: {
            collapseDisallowPrefixes: true,
            additionalDisallow: ["/api/", "/mcp", "/oauth/"],
            contentSignals: { search: true, aiInput: false, aiTrain: false },
        },
        routes: createRoutes(),
    });
    return Object.assign(site, {
        headResolvers: createLogscordPublicHeadExtensions({ siteUrl, defaultImage }),
    });
}
export const LogscordPublicSite = createLogscordPublicSite();
//# sourceMappingURL=manifest.js.map